/**
 * Algolia Analytics Bridge for Hey Dude
 *
 * Global bridge that makes React insights functions available to Liquid templates
 * and vanilla JavaScript components. Adapted from Ted Baker's implementation.
 */

(function() {
  'use strict';

  /**
   * Store search context for checkout tracking
   */
  function storeSearchContext(queryID, productId) {
    try {
      const searchContext = {
        queryID,
        productId,
        timestamp: Date.now()
      };
      sessionStorage.setItem('algolia_search_context', JSON.stringify(searchContext));
      console.log('[Analytics Bridge] Search context stored:', searchContext);
    } catch (error) {
      console.error('[Analytics Bridge] Failed to store search context:', error);
    }
  }

  /**
   * Get stored search context (for checkout tracking)
   */
  function getSearchContext() {
    try {
      const stored = sessionStorage.getItem('algolia_search_context');
      if (!stored) return null;

      const context = JSON.parse(stored);

      // Check if context is still valid (within 30 minutes)
      if (Date.now() - context.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem('algolia_search_context');
        return null;
      }

      return context;
    } catch (error) {
      console.error('[Analytics Bridge] Failed to get search context:', error);
      return null;
    }
  }

  /**
   * Clear search context
   */
  function clearSearchContext() {
    try {
      sessionStorage.removeItem('algolia_search_context');
    } catch (error) {
      console.error('[Analytics Bridge] Failed to clear search context:', error);
    }
  }

  /**
   * Track add to cart events
   * Will delegate to React insights module if available
   */
  function trackAddToCart(productId, queryID = null) {
    try {
      // Check if React insights module is available
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.trackAddToCart) {
        return window.AlgoliaInsightsModule.trackAddToCart(productId, queryID);
      }

      console.log('[Analytics Bridge] Add to cart tracked (fallback):', { productId, queryID });
    } catch (error) {
      console.error('[Analytics Bridge] Failed to track add to cart:', error);
    }
  }

  /**
   * Track product page views
   * Will delegate to React insights module if available
   */
  function trackProductPageView(productId, queryID = null) {
    try {
      // Check if React insights module is available
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.trackProductPageView) {
        return window.AlgoliaInsightsModule.trackProductPageView(productId, queryID);
      }

      console.log('[Analytics Bridge] Product page view tracked (fallback):', { productId, queryID });
    } catch (error) {
      console.error('[Analytics Bridge] Failed to track page view:', error);
    }
  }

  /**
   * Track purchase events
   * Will delegate to React insights module if available
   */
  function trackPurchase(productIds, queryID = null) {
    try {
      const objectIDs = Array.isArray(productIds) ? productIds : [productIds];

      // Check if React insights module is available
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.trackPurchase) {
        return window.AlgoliaInsightsModule.trackPurchase(objectIDs, queryID);
      }

      console.log('[Analytics Bridge] Purchase tracked (fallback):', { productIds, queryID });
    } catch (error) {
      console.error('[Analytics Bridge] Failed to track purchase:', error);
    }
  }

  /**
   * Track quick buy events
   * Will delegate to React insights module if available
   */
  function trackQuickBuy(productId, queryID = null) {
    try {
      // Check if React insights module is available
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.trackCustomEvent) {
        const eventName = queryID ? 'Quick Buy from Search' : 'Quick Buy';
        return window.AlgoliaInsightsModule.trackCustomEvent(eventName, [productId], queryID);
      }

      console.log('[Analytics Bridge] Quick buy tracked (fallback):', { productId, queryID });
    } catch (error) {
      console.error('[Analytics Bridge] Failed to track quick buy:', error);
    }
  }

  /**
   * Handle user authentication
   */
  function setAuthenticatedUser(userId, customerData) {
    try {
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.setAuthenticatedUser) {
        return window.AlgoliaInsightsModule.setAuthenticatedUser(userId, customerData);
      }
      console.log('[Analytics Bridge] User authenticated (fallback):', { userId, customerData });
    } catch (error) {
      console.error('[Analytics Bridge] Failed to set authenticated user:', error);
    }
  }

  function clearAuthenticatedUser() {
    try {
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.clearAuthenticatedUser) {
        return window.AlgoliaInsightsModule.clearAuthenticatedUser();
      }
      console.log('[Analytics Bridge] User logged out (fallback)');
    } catch (error) {
      console.error('[Analytics Bridge] Failed to clear authenticated user:', error);
    }
  }

  function initializeUserFromShopify(customerData) {
    try {
      if (window.AlgoliaInsightsModule && window.AlgoliaInsightsModule.initializeUserFromShopify) {
        return window.AlgoliaInsightsModule.initializeUserFromShopify(customerData);
      }
      console.log('[Analytics Bridge] User initialized from Shopify (fallback):', customerData);
    } catch (error) {
      console.error('[Analytics Bridge] Failed to initialize user from Shopify:', error);
    }
  }

  /**
   * Global API available to Liquid templates and vanilla JS
   */
  window.AlgoliaInsights = {
    trackAddToCart: trackAddToCart,
    trackProductPageView: trackProductPageView,
    trackPurchase: trackPurchase,
    trackQuickBuy: trackQuickBuy,
    storeSearchContext: storeSearchContext,
    getSearchContext: getSearchContext,
    clearSearchContext: clearSearchContext,
    // Auth functions
    setAuthenticatedUser: setAuthenticatedUser,
    clearAuthenticatedUser: clearAuthenticatedUser,
    initializeUserFromShopify: initializeUserFromShopify
  };

  // Also make functions available globally for easier integration
  window.trackAlgoliaAddToCart = trackAddToCart;
  window.trackAlgoliaProductPageView = trackProductPageView;
  window.trackAlgoliaPurchase = trackPurchase;
  window.trackAlgoliaQuickBuy = trackQuickBuy;

  // Auth functions globally available
  window.setAlgoliaAuthenticatedUser = setAuthenticatedUser;
  window.clearAlgoliaAuthenticatedUser = clearAuthenticatedUser;
  window.initAlgoliaUserFromShopify = initializeUserFromShopify;

  console.log('[Analytics Bridge] Global Algolia analytics functions initialized');

})();